<template>
  <!--
    「요금·결제」의 청구 내역 탭(설계안 2판 §10.1 · 선결제 §21.7) — 청구 줄, 거르기(시도일·검색어, 상세에서 넘어온 구독 번호 범위),
    청구 취소(전액·사유 필수 · 구독료 줄은 해지 동반 — Q10·Q11, 사업자 추가 줄은 그 줄만 — §26.6). 남은 기간만 돌려준 중도 해지(구독 상세
    [중도 해지·환불] — §21.10)의 줄은 취소 칸에 「부분 환불 N원 · ○까지」로 보인다. 종류 열·거르기는 없다 — 오더엔 멀티의 사업자 추가
    줄만 금액 칸 안 표지(「사업자 추가 +N곳」)로 보이고, 옛 dev 줄도 같은 칸으로 보인다. 할부 칸은 그 결제에 보낸 할부(「일시불」·「3개월」·
    「3개월 · 무이자」 — 할부 설계 §6), 이 칸 전 줄은 「—」. 말·거르기·요청은 `utils/subscriptionAdmin` 한 곳.
  -->
  <div data-testid="charge-tab">
    <div class="search_form" ref="search_form">
      <slot name="tabs"></slot>
      <el-form :inline="true" :size="size" label-suffix=":" @submit.native.prevent>
        <el-form-item v-if="subscriptionId !== null" :label="$t(text.chargeScope)">
          <span class="charge-scope" data-testid="charge-scope">#{{ subscriptionId }}</span>
          <el-button type="text" data-testid="charge-scope-clear" @click="$emit('clear-scope')">{{
            $t(text.chargeScopeClear)
          }}</el-button>
        </el-form-item>
        <el-form-item :label="$t(text.filterPeriod)">
          <el-date-picker
            v-model="filter.period"
            type="daterange"
            unlink-panels
            clearable
            value-format="yyyy-MM-dd"
            :range-separator="$t('~')"
            :start-placeholder="$t('시작일')"
            :end-placeholder="$t('종료일')"
            :style="{ width: $device.mobile ? `100%` : `250px` }"
            @change="searchFirstPage"></el-date-picker>
        </el-form-item>
        <el-form-item :label="$t(text.filterQuery)">
          <el-input
            v-model="filter.q"
            clearable
            data-testid="charge-filter-q"
            :placeholder="$t(text.filterQueryPlaceholder)"
            :style="{ width: $device.mobile ? `100%` : `300px` }"
            @keyup.enter.native="searchFirstPage"></el-input>
        </el-form-item>
      </el-form>
    </div>

    <div class="buttons" ref="buttons">
      <el-button :size="size" icon="el-icon-search" @click="searchFirstPage">{{ $t('검색') }}</el-button>
    </div>

    <!-- 핵심 칸이 앞이다 — 매장·금액·결과·동작이 1024×768 의 표 폭 안에, 기간·요금제·할부·시도 시각·취소 기록은 그 뒤(넘치면 스크롤) -->
    <el-table
      :height="tableHeight - 48"
      border
      ref="table"
      row-key="chargeId"
      :data="tableData"
      :size="size"
      :empty-text="$t(text.chargesEmpty)"
      :default-sort="{ prop: defaultSort.sortProp, order: defaultSort.sortOrder }"
      @sort-change="handleSortChange">
      <el-table-column :label="$t(text.columnStore)" min-width="140">
        <template slot-scope="scope">
          <!-- 누구의 구독인가 — POS2 Lite 는 매장(번호), 오더엔 멀티는 사장님(「사장님」 · 휴대폰) + 구독 번호 -->
          <div class="charge-store" data-testid="charge-subscriber">
            {{ subscriberCellOf(scope.row, [`#${scope.row.subscriptionId}`]).name || '—' }}
          </div>
          <div class="charge-store__id" data-testid="charge-subscriber-id">
            {{
              subscriberCellOf(scope.row, [`#${scope.row.subscriptionId}`])
                .tags.map((tag) => $t(tag))
                .join(' · ')
            }}
          </div>
        </template>
      </el-table-column>
      <el-table-column :label="$t(text.columnAmount)" min-width="110" align="right">
        <template slot-scope="scope">
          <div data-testid="charge-amount">{{ amountLinesOf(scope.row.amount, scope.row.listAmount).main }}</div>
          <div class="charge-store__id">{{ amountLinesOf(scope.row.amount, scope.row.listAmount).list }}</div>
          <div v-if="chargeAddonMarkOf(scope.row)" class="charge-addon" data-testid="charge-addon">
            <SubscriptionBadge :tone="chargeAddonMarkOf(scope.row).tone" outline>{{
              $t(chargeAddonMarkOf(scope.row).label)
            }}</SubscriptionBadge>
            <span class="charge-store__id">{{ chargeAddonMarkOf(scope.row).count }}</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column :label="$t(text.columnResult)" min-width="140">
        <template slot-scope="scope">
          <div data-testid="charge-result">
            <SubscriptionBadge :tone="chargeResultViewOf(scope.row).tone">{{
              $t(chargeResultViewOf(scope.row).label)
            }}</SubscriptionBadge>
            <div v-if="chargeResultViewOf(scope.row).note" class="charge-result__note">
              {{ chargeResultViewOf(scope.row).note }}
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column :label="$t(text.columnActions)" width="112" align="center">
        <template slot-scope="scope">
          <el-button
            v-if="chargeCancelable(scope.row, checkAuthActionAccess)"
            size="mini"
            plain
            type="danger"
            data-testid="charge-cancel"
            @click="openCancel(scope.row)"
            >{{ $t(text.cancelCharge) }}</el-button
          >
        </template>
      </el-table-column>
      <el-table-column :label="$t(text.columnPeriod)" min-width="180" align="center">
        <template slot-scope="scope">
          {{ chargePeriodText(scope.row) || '—' }}
          <div v-if="chargeBonusText(scope.row)" class="charge-result__note" data-testid="charge-bonus">
            {{ chargeBonusText(scope.row) }}
          </div>
        </template>
      </el-table-column>
      <el-table-column :label="$t(text.columnPlan)" width="100" align="center">
        <template slot-scope="scope">{{ planText(scope.row.planCode) || '—' }}</template>
      </el-table-column>
      <el-table-column :label="$t(text.columnInstallment)" width="110" align="center">
        <template slot-scope="scope">
          <span data-testid="charge-installment">{{ chargeInstallmentText(scope.row) || '—' }}</span>
        </template>
      </el-table-column>
      <el-table-column
        :label="$t(text.columnRequested)"
        prop="requestedDtm"
        width="140"
        align="center"
        sortable="custom">
        <template slot-scope="scope">{{ dateTimeText(scope.row.requestedDtm) }}</template>
      </el-table-column>
      <el-table-column :label="$t(text.columnCancel)" min-width="180">
        <template slot-scope="scope">
          <template v-if="scope.row.canceledDtm">
            <div v-if="chargeRefundText(scope.row)" data-testid="charge-partial-refund">
              {{ chargeRefundText(scope.row) }}
            </div>
            {{ dateTimeText(scope.row.canceledDtm) }} · {{ scope.row.cancelBy }}
            <div class="charge-result__note">{{ scope.row.cancelReason }}</div>
          </template>
          <template v-else>—</template>
        </template>
      </el-table-column>
      <el-table-column :label="$t(text.columnChargeId)" prop="chargeId" width="90" align="center"></el-table-column>
    </el-table>

    <list-pagination
      :search-form="searchForm"
      :total="totalElements"
      @page-change="handlePageChange"
      @size-change="handleSizeChange"></list-pagination>

    <el-dialog
      :title="$t(text.cancelTitle)"
      :visible.sync="cancel.visible"
      width="440px"
      append-to-body
      data-testid="charge-cancel-dialog"
      :close-on-click-modal="false">
      <p class="charge-cancel__text" data-testid="charge-cancel-text">
        {{ cancel.row ? chargeCancelText(cancel.row) : '' }}
      </p>
      <el-form label-position="top" @submit.native.prevent>
        <el-form-item :label="$t(text.cancelReason)" :error="cancel.problem">
          <el-input
            v-model="cancel.reason"
            type="textarea"
            :rows="3"
            data-testid="charge-cancel-reason"
            :placeholder="$t(text.reasonPlaceholder)"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button :size="size" @click="cancel.visible = false">{{ $t(text.close) }}</el-button>
        <el-button
          :size="size"
          type="danger"
          data-testid="charge-cancel-submit"
          :loading="cancel.busy"
          @click="submitCancel"
          >{{ $t(text.cancelCharge) }}</el-button
        >
      </span>
    </el-dialog>
  </div>
</template>

<script>
import SubscriptionBadge from '@/components/subscription/SubscriptionBadge.vue'
import mainVueMixin from '@/mixins/mainVueMixin'
import pagedListMixin from '@/mixins/pagedListMixin'
import { httpErrorMessage } from '@/plugins/http/policy'
import { initData } from '@/utils/constants'
import { withPaging } from '@/utils/pagedList'
import {
  CHARGE_DEFAULT_SORT,
  SUBSCRIPTION_CHARGE_API,
  SUBSCRIPTION_TEXT,
  SUBSCRIPTION_WRITE_CONFIG,
  amountLinesOf,
  chargeAddonMarkOf,
  chargeCancelOutcomeOf,
  chargeCancelRequestOf,
  chargeCancelText,
  chargeCancelable,
  chargeListQuery,
  chargeBonusText,
  chargeInstallmentText,
  chargePeriodText,
  chargeRefundText,
  chargeResultViewOf,
  dateTimeText,
  planText,
  subscriberCellOf,
} from '@/utils/subscriptionAdmin'

const closedCancel = () => ({ visible: false, row: null, reason: '', problem: '', busy: false })

export default {
  name: 'SubscriptionChargeTable',

  mixins: [mainVueMixin, pagedListMixin],

  components: { SubscriptionBadge },

  props: {
    /** 상세 창의 [청구 내역]에서 넘어온 범위 — 없으면 전체 */
    subscriptionId: { type: Number, default: null },
  },

  data: function () {
    var data = initData()
    data.url = SUBSCRIPTION_CHARGE_API
    data.text = SUBSCRIPTION_TEXT
    data.defaultSort = CHARGE_DEFAULT_SORT
    data.searchForm = Object.assign(withPaging({}), CHARGE_DEFAULT_SORT)
    data.filter = { period: null, q: '' }
    data.cancel = closedCancel()

    return data
  },

  watch: {
    subscriptionId: function () {
      this.searchFirstPage()
    },
  },

  created: function () {
    this.searchFirstPage()
  },

  methods: {
    amountLinesOf,
    chargeAddonMarkOf,
    chargeCancelText,
    chargeCancelable,
    chargeBonusText,
    chargeInstallmentText,
    chargePeriodText,
    chargeRefundText,
    chargeResultViewOf,
    dateTimeText,
    planText,
    subscriberCellOf,

    search: function () {
      const filter = Object.assign({}, this.filter, { subscriptionId: this.subscriptionId })

      this.mainVue.$axios.search(
        this.url,
        chargeListQuery(filter, this.searchQuery()),
        function (result) {
          this.applyPage(result)
        }.bind(this)
      )
    },

    openCancel: function (row) {
      this.cancel = Object.assign(closedCancel(), { visible: true, row })
    },

    /**
     * 청구 취소 — 보내고, 결과를 말하고, 다시 읽는다. 헥토가 취소하지 않은 갈래는 200 값이다(줄의 `canceledDtm` 이 빔) — 창 안에서
     * 그 사유를 말하고 목록은 다시 읽는다(취소 결과 칸이 찼다). 409(그 사이 취소할 수 없게 됨)도 그 문장을 말하고 다시 읽는다.
     */
    submitCancel: async function () {
      const request = chargeCancelRequestOf(this.cancel.row.chargeId, { reason: this.cancel.reason })
      if (!request.ok) {
        this.cancel.problem = this.$t(request.problem)
        return
      }

      this.cancel.busy = true
      this.cancel.problem = ''
      try {
        const response = await this.$axios.post(request.url, request.body, SUBSCRIPTION_WRITE_CONFIG)
        const outcome = chargeCancelOutcomeOf(response && response.data)

        if (outcome.done) {
          this.cancel.visible = false
          this.mainVue.$message({ type: 'success', message: this.$t(outcome.message) })
        } else {
          this.cancel.problem = this.$t(outcome.message)
        }
        this.search()
      } catch (error) {
        this.cancel.problem = httpErrorMessage(error, this.$t(this.text.writeFailed))
        if (error && error.status === 409) this.search()
      } finally {
        this.cancel.busy = false
      }
    },
  },
}
</script>

<style scoped>
.charge-scope {
  margin-right: 8px;
  font-weight: 500;
}

.charge-store {
  font-weight: 500;
  color: var(--color-text-primary);
}

.charge-store__id,
.charge-result__note {
  font-size: 12px;
  color: var(--color-text-secondary);
}

.charge-addon {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
}

.charge-cancel__text {
  margin: 0 0 12px;
  color: var(--color-text-primary);
  word-break: keep-all;
}
</style>
